import { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, FileText, Search, CreditCard, Users, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { manualPaymentAPI, bankStatementAPI, clientAPI } from "@/lib/api";
import { DateField } from "@/components/ui/date-field";
import { isChequeMode, usePaymentMethods } from "@/lib/paymentMethods";

const formatEur = (v) => `${(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} \u20ac`;

/* ── Sub-components ─────────────────────────────────────────────────── */

function InvoiceAssignRow({ inv, isChecked, onToggle, assignedAmount, onUpdateAmount, showClientBadge }) {
  const invRemaining = inv.remaining ?? (inv.total_ttc - (inv.total_paid || 0));
  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 rounded-md border transition-all ${isChecked ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-slate-300"}`}>
      <input type="checkbox" checked={isChecked} onChange={onToggle} className="accent-indigo-600 shrink-0" data-testid={`mp-check-${inv.number}`} />
      <FileText size={12} className="text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-mono text-xs font-medium">{inv.number}</span>
        {showClientBadge && <Badge className="bg-indigo-100 text-indigo-700 text-[8px] px-1 py-0 ml-1">{inv.client_name}</Badge>}
        <span className="text-[10px] text-slate-400 ml-2">{inv.date}</span>
      </div>
      <div className="text-right text-xs shrink-0">
        <span className="text-slate-600">{formatEur(inv.total_ttc)}</span>
        <span className="text-[10px] text-slate-400 ml-1">reste {formatEur(invRemaining)}</span>
      </div>
      {isChecked && (
        <div className="relative w-[90px] shrink-0">
          <input type="number" step="0.01" min="0.01" max={invRemaining}
            value={assignedAmount}
            onChange={e => onUpdateAmount(e.target.value)}
            className="w-full h-7 px-2 pr-6 text-xs font-medium border border-indigo-300 rounded focus:border-indigo-500 outline-none"
            data-testid={`mp-amount-${inv.number}`} />
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-slate-400">EUR</span>
        </div>
      )}
    </div>
  );
}

function ClientSelectionStep({ clientSearch, setClientSearch, searchingClients, clientResults, selectedClient, setSelectedClient, onNext }) {
  return (
    <div className="space-y-3" data-testid="manual-payment-step-0">
      <p className="text-xs text-slate-500 uppercase tracking-wider">Sélectionnez un client</p>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" placeholder="Rechercher un client..." value={clientSearch}
          onChange={e => setClientSearch(e.target.value)}
          className="w-full h-9 pl-8 pr-3 text-sm border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none"
          autoFocus data-testid="manual-payment-client-search" />
      </div>
      {searchingClients && <div className="flex justify-center py-2"><Loader2 className="animate-spin h-4 w-4 text-slate-400" /></div>}
      {clientResults.length > 0 && (
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {clientResults.map(c => (
            <button key={c.id} onClick={() => { setSelectedClient(c); onNext(); }}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-left transition-all hover:border-indigo-400 hover:bg-indigo-50 ${selectedClient?.id === c.id ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}`}
              data-testid={`client-option-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.company || c.nom_etablissement || ""}</p>
                <p className="text-[10px] text-slate-400">{c.first_name} {c.last_name}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {selectedClient && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-2 flex items-center gap-2">
          <Check size={14} className="text-indigo-600" />
          <span className="text-sm font-medium text-indigo-800">{selectedClient.company || selectedClient.nom_etablissement}</span>
          <Button variant="ghost" size="sm" className="ml-auto h-6 text-[10px]" onClick={() => setSelectedClient(null)}>Changer</Button>
        </div>
      )}
      <div className="flex justify-end pt-2">
        <Button size="sm" disabled={!selectedClient} onClick={onNext} data-testid="manual-payment-next-step1">Suivant</Button>
      </div>
    </div>
  );
}

function ChequeAttachment({ chequeFile, setChequeFile }) {
  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/", "application/pdf"];
    if (!allowed.some((p) => file.type.startsWith(p))) {
      toast.error("Format non supporté (image ou PDF uniquement)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 5 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const base64 = String(result).split(",")[1] || "";
      setChequeFile({ name: file.name, mime: file.type, base64, size: file.size });
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="rounded-md border border-dashed border-indigo-300 bg-indigo-50/40 p-3" data-testid="cheque-attachment">
      <div className="flex items-center gap-2 mb-2">
        <Paperclip size={13} className="text-indigo-600" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">Photo / PDF du chèque (optionnel)</span>
      </div>
      {chequeFile ? (
        <div className="flex items-center gap-2 text-xs text-slate-700">
          <FileText size={14} className="text-indigo-600 shrink-0" />
          <span className="flex-1 truncate" title={chequeFile.name}>{chequeFile.name}</span>
          <span className="text-[10px] text-slate-400">{(chequeFile.size / 1024).toFixed(0)} Ko</span>
          <button type="button" onClick={() => setChequeFile(null)} className="text-slate-400 hover:text-red-500" data-testid="cheque-remove-btn">
            <X size={14} />
          </button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 h-9 cursor-pointer rounded-md bg-white border border-indigo-200 text-xs text-indigo-700 hover:bg-indigo-50" data-testid="cheque-pick-btn">
          <Paperclip size={12} />
          <span>Joindre une image ou un PDF</span>
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPick} />
        </label>
      )}
    </div>
  );
}

function PaymentDetailsStep({ form, setForm, pm, selectedClient, clientId, submitting, onBack, onSaveOnly, onNext, chequeFile, setChequeFile }) {
  return (
    <div className="space-y-3" data-testid="manual-payment-step-1">
      {selectedClient && (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <span className="text-xs text-slate-500">Client : </span>
          <span className="text-sm font-medium">{selectedClient.company || selectedClient.nom_etablissement}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Date</label>
          <DateField value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
            className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none" data-testid="manual-payment-date" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Montant</label>
          <div className="relative">
            <input type="number" step="0.01" min="0.01" placeholder="0.00" value={form.amount}
              onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              className="w-full h-9 px-3 pr-8 text-sm font-medium border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none" autoFocus data-testid="manual-payment-amount" />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">EUR</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Mode</label>
          <select value={form.mode} onChange={e => setForm(p => ({ ...p, mode: e.target.value }))}
            className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none bg-white" data-testid="manual-payment-mode">
            {pm.options.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Référence</label>
          <input type="text" placeholder="ex: VIR 123456" value={form.reference}
            onChange={e => setForm(p => ({ ...p, reference: e.target.value }))}
            className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none" data-testid="manual-payment-reference" />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Commentaire</label>
        <input type="text" placeholder="ex: Règlement Mr Ellia" value={form.comment}
          onChange={e => setForm(p => ({ ...p, comment: e.target.value }))}
          className="w-full h-9 px-3 text-sm border border-slate-300 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-300 outline-none" data-testid="manual-payment-comment" />
      </div>
      {isChequeMode(form.mode) && (
        <ChequeAttachment chequeFile={chequeFile} setChequeFile={setChequeFile} />
      )}
      <div className="flex justify-between pt-2">
        {!clientId ? <Button variant="outline" size="sm" onClick={onBack}>Retour</Button> : <div />}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onSaveOnly} disabled={!parseFloat(form.amount) || submitting} data-testid="manual-payment-save-only">
            Enregistrer sans affecter
          </Button>
          <Button size="sm" onClick={onNext} disabled={!parseFloat(form.amount)} data-testid="manual-payment-next-step2">
            Affecter aux factures
          </Button>
        </div>
      </div>
    </div>
  );
}

function InvoiceAssignmentStep({ form, pm, paymentAmount, totalAssigned, remaining, assignments, currentInvs, otherInvs, loadingInvoices, invoices, submitting, onToggle, onUpdateAmount, onBack, onSubmit }) {
  return (
    <div className="space-y-3" data-testid="manual-payment-step-2">
      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs text-indigo-600 uppercase tracking-wider">Règlement</p>
            <p className="text-sm text-slate-700">{form.date} - {pm.label(form.mode)} {form.reference && `- ${form.reference}`}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-indigo-700">{formatEur(paymentAmount)}</p>
            {totalAssigned > 0 && (
              <p className={`text-[11px] font-medium ${remaining > 0.01 ? "text-amber-600" : "text-emerald-600"}`}>
                Affecte: {formatEur(totalAssigned)} | Reste: {formatEur(remaining)}
              </p>
            )}
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider">Sélectionnez les factures a affecter</p>
      {loadingInvoices ? (
        <div className="flex justify-center py-4"><Loader2 className="animate-spin h-4 w-4 text-slate-400" /></div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">Aucune facture impayee</p>
      ) : (
        <div className="max-h-[300px] overflow-y-auto space-y-1" data-testid="manual-payment-invoices">
          {currentInvs.length > 0 && (
            <div className="space-y-1 mb-1">
              {currentInvs.map(inv => (
                <InvoiceAssignRow key={inv.id} inv={inv} isChecked={assignments[inv.id] !== undefined}
                  onToggle={() => onToggle(inv)} assignedAmount={assignments[inv.id]}
                  onUpdateAmount={val => onUpdateAmount(inv.id, val)} showClientBadge={false} />
              ))}
            </div>
          )}
          {otherInvs.length > 0 && (
            <>
              <div className="flex items-center gap-2 my-1.5 py-1.5 border-t border-b border-indigo-200 bg-indigo-50/50 px-2 rounded">
                <Users size={12} className="text-indigo-600" />
                <span className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wider">Autres sociétés</span>
                <Badge className="bg-indigo-600 text-white text-[9px] px-1.5 py-0">{otherInvs.length}</Badge>
              </div>
              <div className="space-y-1">
                {otherInvs.map(inv => (
                  <InvoiceAssignRow key={inv.id} inv={inv} isChecked={assignments[inv.id] !== undefined}
                    onToggle={() => onToggle(inv)} assignedAmount={assignments[inv.id]}
                    onUpdateAmount={val => onUpdateAmount(inv.id, val)} showClientBadge={true} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {totalAssigned > 0 && (
        <div className={`rounded-md p-2.5 border ${remaining > 0.01 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="mp-summary">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-600">{Object.keys(assignments).length} facture(s) selectionnee(s)</span>
            <div className="flex gap-3">
              <span className="font-medium text-emerald-700">Affecte: {formatEur(totalAssigned)}</span>
              {remaining > 0.01 && <span className="font-medium text-amber-700">Reste: {formatEur(remaining)}</span>}
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between pt-1">
        <Button variant="outline" size="sm" onClick={onBack}>Retour</Button>
        <Button size="sm" onClick={onSubmit} disabled={submitting} data-testid="manual-payment-submit">
          {submitting ? <Loader2 className="animate-spin h-4 w-4 mr-1" /> : <Check size={14} className="mr-1" />}
          Valider le règlement
        </Button>
      </div>
    </div>
  );
}

/* ── Main dialog ──────────────────────────────────────────────────── */

export function ManualPaymentDialog({ open, onOpenChange, clientId, clientName, onSuccess }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ date: new Date().toISOString().split("T")[0], amount: "", mode: "", reference: "", comment: "" });
  const [selectedClient, setSelectedClient] = useState(clientId ? { id: clientId, company: clientName || "" } : null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [searchingClients, setSearchingClients] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [assignments, setAssignments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [chequeFile, setChequeFile] = useState(null);
  const pm = usePaymentMethods();
  const effForm = useMemo(() => ({ ...form, mode: form.mode || pm.defaultValue }), [form, pm.defaultValue]);

  useEffect(() => {
    if (open) {
      setStep(clientId ? 1 : 0);
      setForm({ date: new Date().toISOString().split("T")[0], amount: "", mode: "", reference: "", comment: "" });
      setSelectedClient(clientId ? { id: clientId, company: clientName || "" } : null);
      setClientSearch(""); setClientResults([]); setInvoices([]); setAssignments({});
      setChequeFile(null);
    }
  }, [open, clientId, clientName]);

  const searchClients = useCallback(async (q) => {
    if (!q || q.length < 2) { setClientResults([]); return; }
    setSearchingClients(true);
    try {
      const r = await clientAPI.list({ search: q, limit: 10 });
      setClientResults((r.data?.items || r.data || []).slice(0, 8));
    } catch { setClientResults([]); }
    setSearchingClients(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchClients(clientSearch), 300);
    return () => clearTimeout(t);
  }, [clientSearch, searchClients]);

  const loadInvoices = useCallback(async () => {
    if (!selectedClient?.id) return;
    setLoadingInvoices(true);
    try {
      const r = await bankStatementAPI.clientRelatedUnpaid(selectedClient.id);
      setInvoices(r.data || []);
    } catch { setInvoices([]); }
    setLoadingInvoices(false);
  }, [selectedClient]);

  const totalAssigned = useMemo(() => Object.values(assignments).reduce((s, v) => s + (parseFloat(v) || 0), 0), [assignments]);
  const paymentAmount = parseFloat(form.amount) || 0;
  const remaining = Math.max(paymentAmount - totalAssigned, 0);

  const goToStep2 = () => {
    if (!paymentAmount || paymentAmount <= 0) { toast.error("Montant invalide"); return; }
    if (!selectedClient?.id) { toast.error("Sélectionnez un client"); return; }
    setStep(2);
    loadInvoices();
  };

  const toggleInvoice = (inv) => {
    const invId = inv.id;
    if (assignments[invId] !== undefined) {
      const next = { ...assignments };
      delete next[invId];
      setAssignments(next);
    } else {
      const invRemaining = inv.remaining ?? (inv.total_ttc - (inv.total_paid || 0));
      const currentRemaining = paymentAmount - Object.entries(assignments).reduce((s, [k, v]) => k !== invId ? s + (parseFloat(v) || 0) : s, 0);
      setAssignments(prev => ({ ...prev, [invId]: Math.max(Math.min(invRemaining, currentRemaining), 0).toFixed(2) }));
    }
  };

  const updateAssignAmount = (invId, val) => setAssignments(prev => ({ ...prev, [invId]: val }));

  const submit = async () => {
    if (totalAssigned > paymentAmount + 0.01) { toast.error("Le total affecte dépasse le montant du règlement"); return; }
    setSubmitting(true);
    try {
      const assignmentsList = Object.entries(assignments)
        .filter(([, v]) => parseFloat(v) > 0)
        .map(([invoiceId, amount]) => ({ invoice_id: invoiceId, amount: parseFloat(amount) }));
      await manualPaymentAPI.create({
        client_id: selectedClient.id, date: form.date, amount: paymentAmount,
        mode: effForm.mode, reference: form.reference, comment: form.comment,
        assignments: assignmentsList,
        cheque_image_base64: isChequeMode(effForm.mode) ? (chequeFile?.base64 || "") : "",
        cheque_image_mime: isChequeMode(effForm.mode) ? (chequeFile?.mime || "") : "",
      });
      const count = assignmentsList.length;
      toast.success(`Règlement de ${formatEur(paymentAmount)} cree${count > 0 ? ` et affecte a ${count} facture${count > 1 ? "s" : ""}` : ""}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    }
    setSubmitting(false);
  };

  const currentInvs = useMemo(() => invoices.filter(i => i.is_current_client), [invoices]);
  const otherInvs = useMemo(() => invoices.filter(i => !i.is_current_client), [invoices]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <CreditCard size={16} className="text-indigo-600" />
            Nouveau règlement manuel
            {step > 0 && <Badge className="bg-indigo-100 text-indigo-700 text-[9px] px-1.5 py-0">Etape {step}/2</Badge>}
          </DialogTitle>
        </DialogHeader>
        {step === 0 && (
          <ClientSelectionStep clientSearch={clientSearch} setClientSearch={setClientSearch}
            searchingClients={searchingClients} clientResults={clientResults}
            selectedClient={selectedClient} setSelectedClient={setSelectedClient}
            onNext={() => setStep(1)} />
        )}
        {step === 1 && (
          <PaymentDetailsStep form={effForm} setForm={setForm} pm={pm}
            selectedClient={selectedClient} clientId={clientId} submitting={submitting}
            onBack={() => setStep(0)} onSaveOnly={submit} onNext={goToStep2}
            chequeFile={chequeFile} setChequeFile={setChequeFile} />
        )}
        {step === 2 && (
          <InvoiceAssignmentStep form={effForm} pm={pm} paymentAmount={paymentAmount}
            totalAssigned={totalAssigned} remaining={remaining}
            assignments={assignments} currentInvs={currentInvs} otherInvs={otherInvs}
            loadingInvoices={loadingInvoices} invoices={invoices} submitting={submitting}
            onToggle={toggleInvoice} onUpdateAmount={updateAssignAmount}
            onBack={() => setStep(1)} onSubmit={submit} />
        )}
      </DialogContent>
    </Dialog>
  );
}
